import { useMemo } from 'react';
import { Edit } from '@mui/icons-material';
import { Button } from '@mui/material';
import { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CustomerFragmentFragment } from '../../../../api/gql';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { getRemindingToneTransKey } from '../../../../utils/invoice';

type Props = {
  customers?: CustomerFragmentFragment[];
} & Partial<DataGridProps>;

export const CustomersTable = ({ customers, ...props }: Props) => {
  const { t } = useTranslation();
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('name') as string,
        flex: 1,
        minWidth: 200,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vatId',
        headerName: t('vatId') as string,
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'taxId',
        headerName: t('taxId') as string,
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'phoneNumber',
        headerName: t('phone') as string,
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'email',
        headerName: t('email') as string,
        flex: 1,
        minWidth: 150,
        maxWidth: 200,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'address',
        headerName: t('address') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'remindingTone',
        headerName: t('remindingTone.title') as string,
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        valueGetter: (params) => {
          return t(getRemindingToneTransKey(params.row.remindingTone));
        },
      },
      {
        field: 'action',
        headerName: '',
        minWidth: 150,
        maxWidth: 150,
        flex: 1,
        align: 'right',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            startIcon={<Edit />}
            variant={'contained'}
            component={Link}
            to={`/contacts/customers/edit/${params.row.id}`}
          >
            {t('edit')}
          </Button>
        ),
      },
    ],
    [t],
  );

  return <Table id={'customers'} autoHeight {...props} columns={columns} rows={customers ?? []} />;
};
